import { transformContext } from '@openfeature/config-cat-core'
import type { ConfigCatWebProvider } from '@openfeature/config-cat-web-provider'
import type { EvaluationContext, JsonValue } from '@openfeature/web-sdk'

/**
 * The evaluation context as ConfigCat has to receive it.
 *
 * `transformContext` in @openfeature/config-cat-core keeps an attribute only
 * when its value is truthy, so any number that is 0 never reaches ConfigCat. A
 * visitor on their install day arrives with no daysSinceInstall, misses every
 * rule that reads it and falls through to the cohort split, while the server
 * render, working from the group's bucket, picks the rule. ConfigCat compares a
 * numeric string exactly as it compares the number, so numbers go as digits.
 *
 * Only what ConfigCat sees changes. The OpenFeature context itself keeps its
 * numbers, because analytics events copy it as they are.
 */
export function toConfigCatContext(
  context: EvaluationContext
): EvaluationContext {
  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : value,
    ])
  )
}

/** transformContext for a context this app built, see toConfigCatContext. */
export function toConfigCatUser(context: EvaluationContext) {
  return transformContext(toConfigCatContext(context))
}

/**
 * Routes every browser evaluation through toConfigCatContext. The provider
 * transforms the context itself on each read, and more than one place writes
 * the OpenFeature context, so this is the one spot that covers all of them.
 */
export function withConfigCatContext(
  provider: ConfigCatWebProvider
): ConfigCatWebProvider {
  const resolveBoolean = provider.resolveBooleanEvaluation.bind(provider)
  const resolveString = provider.resolveStringEvaluation.bind(provider)
  const resolveNumber = provider.resolveNumberEvaluation.bind(provider)
  const resolveObject = provider.resolveObjectEvaluation.bind(provider)

  provider.resolveBooleanEvaluation = (flagKey, defaultValue, context) =>
    resolveBoolean(flagKey, defaultValue, toConfigCatContext(context))
  provider.resolveStringEvaluation = (flagKey, defaultValue, context) =>
    resolveString(flagKey, defaultValue, toConfigCatContext(context))
  provider.resolveNumberEvaluation = (flagKey, defaultValue, context) =>
    resolveNumber(flagKey, defaultValue, toConfigCatContext(context))
  provider.resolveObjectEvaluation = <U extends JsonValue>(
    flagKey: string,
    defaultValue: U,
    context: EvaluationContext
  ) => resolveObject(flagKey, defaultValue, toConfigCatContext(context))
  return provider
}
